import { GridCellProps, GridPagerSettings } from '@progress/kendo-react-grid'
import moment from 'moment'
import { JSXElementConstructor, ReactElement, cloneElement, useState } from 'react'
import { getBaseURL } from '../../../GlobalComponents/URLs'
import { ReturnIcon } from '../../../GlobalComponents/_icons/Icons'
import InsightLogo from '../../../LCMForms/Images/Insight.png'

import {
    AggregateDescriptor,
    State,
    compileFilter,
    groupBy,
    normalizeFilters,
    orderBy,
} from '@progress/kendo-data-query'
import { setGroupIds } from '@progress/kendo-react-data-tools'
import { isPresent } from '@progress/kendo-react-layout'
import { count, limit } from '@progress/kendo-data-query/dist/npm/array.operators'
import { filter } from '@progress/kendo-data-query/dist/npm/transducers'
import { compose } from '@progress/kendo-data-query/dist/npm/funcs'
import { TextAreaModal } from '../../../GlobalComponents/GenericModals/GenericModals'

export function formatCurrency(number: number) {
    const result = number.toLocaleString('en-US', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })
    return '$' + result
}

export function formatDate(date: Date): string {
    let formattedDate = moment.utc(date).format('MM/DD/YY')
    if (formattedDate === '01/01/01' || formattedDate === 'Invalid date') {
        formattedDate = ''
    }
    return formattedDate
}

export const openViewSalesOrderWindow = (props: GridCellProps) => {
    const baseURL = getBaseURL()
    const url = `${baseURL}OPForms/OrderEntry.aspx?Type=View&IsCommission=False&IsQuote=False&OrderNumber=${props.dataItem.OrderNumber}`
    const w = window as any
    w.showHTMLModalDialog(
        url,
        450,
        1010,
        '',
        `Sales Order - ${props.dataItem.OrderNumber}`,
        false,
        false,
        null
    )
}

export const openInInsight = (props: GridCellProps) => {
    const baseURL = getBaseURL()
    const url = `${baseURL}lcmforms?od=${props.dataItem.OrderDetailId}`
    const w = window as any
    w.showHTMLModalDialog(
        url,
        745,
        1630,
        '',
        `Insight - ${props.dataItem.OrderNumber}-${props.dataItem.LineItemNumber}`,
        false,
        false,
        null
    )
}

export const returnSalesOrderLineItemInformation = (props: GridCellProps) => {
    const w = window as any
    const s = String.fromCharCode(6)
    w.returnValue = `${props.dataItem.OrderNumber}${s}${props.dataItem.OrderDetailId}${s}${props.dataItem.LineItemNumber}`
    w.quitWindow()
}

export const cellShortDate = (cellProps: GridCellProps) => {
    if (cellProps.rowType === 'data' && cellProps.field) {
        const value = cellProps.dataItem[cellProps.field]
        return <td style={{ textAlign: 'center' }}>{value && formatDate(value)}</td>
    } else {
        return <></>
    }
}

export const cellCurrency = (cellProps: GridCellProps) => {
    if (cellProps.rowType === 'data' && cellProps.field) {
        const value = cellProps.dataItem[cellProps.field]
        return <td style={{ textAlign: 'right' }}>{value ? formatCurrency(value) : '$0.00'}</td>
    } else {
        return <></>
    }
}

export const cellRender = (
    tdElement: ReactElement<HTMLTableCellElement, string | JSXElementConstructor<any>> | null,
    cellProps: GridCellProps
) => {
    if (cellProps.rowType === 'groupHeader') {
        if (tdElement) {
            const lightBlue = { backgroundColor: '#d9e1f2' }
            const trProps: any = { style: lightBlue }
            return cloneElement(tdElement, { ...trProps }, tdElement.props.children)
        }
    }
    return tdElement
}

export const cellSalesOrderWindow = (cellProps: GridCellProps) => {
    if (cellProps.rowType === 'data') {
        return (
            <td colSpan={1} className='' role='gridCell' aria-colindex={3} data-grid-col-index='2'>
                <span
                    onClick={() => openViewSalesOrderWindow(cellProps)}
                    style={{ color: '#0062FF', cursor: 'pointer' }}
                >
                    {cellProps.dataItem.OrderNumber}
                </span>
            </td>
        )
    } else {
        return <></>
    }
}

export const cellReturnOrderDetailId = (cellProps: GridCellProps) => {
    if (cellProps.rowType === 'data') {
        return (
            <td colSpan={1} className='' role='gridCell' aria-colindex={0} data-grid-col-index='0'>
                <a
                    onClick={() => returnSalesOrderLineItemInformation(cellProps)}
                    href='#'
                    title='Copy Assembly To Order'
                >
                    <ReturnIcon />
                </a>
            </td>
        )
    } else {
        return <></>
    }
}

export const cellOpenInsight = (cellProps: GridCellProps) => {
    if (cellProps.rowType === 'data') {
        return (
            <td colSpan={1} className='' role='gridCell' aria-colindex={3} data-grid-col-index='3'>
                {cellProps.dataItem.HasTagUID && (
                    <img
                        src={InsightLogo}
                        style={{ display: 'flex', width: '35px', cursor: 'pointer' }}
                        alt='Insight'
                        title='Open Valve in Insight'
                        onClick={() => openInInsight(cellProps)}
                    />
                )}
            </td>
        )
    } else {
        return <></>
    }
}
export const cellOpenNotes = (cellProps: GridCellProps) => {
    if (cellProps.rowType === 'data') {
        const [notesModalOpen, setNotesModalOpen] = useState<boolean>(false)
        let notesFieldPretty =
            cellProps.dataItem.NotesField === 'QuoteNote' ? 'Quote Notes' : 'Line Item Notes'
        let notes = cellProps.dataItem.Notes

        return (
            <td
                colSpan={1}
                className=''
                style={{ textAlign: 'center' }}
                role='gridCell'
                aria-colindex={3}
                data-grid-col-index='3'
            >
                <div
                    className='k-icon k-i-txt'
                    onClick={() => {
                        setNotesModalOpen(true)
                    }}
                    style={{
                        cursor: 'pointer',
                    }}
                />
                <TextAreaModal
                    title={`Sales Order # ${cellProps.dataItem.OrderNumber} Line Item # ${cellProps.dataItem.LineItemNumber} - ${notesFieldPretty}`}
                    close={() => {
                        setNotesModalOpen(false)
                    }}
                    open={notesModalOpen}
                    defaultValue={notes}
                />
            </td>
        )
    } else {
        return <></>
    }
}

export const processWithGroups = (
    data: any[],
    dataState: State,
    aggregates: AggregateDescriptor[]
) => {
    const groups = dataState.group
    if (groups) {
        groups.map((group) => (group.aggregates = aggregates))
    }
    dataState.group = groups
    const newDataState = customProcess(data, dataState)
    setGroupIds({ data: newDataState.data, group: dataState.group })

    return newDataState
}

export const processDataState = (data: any[], dataState: State) => {
    const newDataState = customProcess(data, dataState)
    setGroupIds({ data: newDataState.data, group: dataState.group })

    return newDataState
}

export const createAppState = (skip: number, take: number, data: any) => {
    let pagerSettings: GridPagerSettings = {
        buttonCount: 5,
        info: true,
        type: 'input',
        pageSizes: [5, 10, 20, 100, 1000],
        previousNext: true,
    }
    return {
        items: data.slice(skip, skip + take),
        total: data.length,
        skip: skip,
        pageSize: take,
        pageable: pagerSettings,
    }
}

export const sortData = (data: AssemblySearchResult[]) => {
    return data.sort((a, b) => {
        return new Date(b.OrderEntryDate).getTime() - new Date(a.OrderEntryDate).getTime()
    })
}

var customProcess = function (data: any[], state: State) {
    var skipCount = state.skip,
        takeCount = state.take,
        filterDescriptor = state.filter,
        sort = state.sort,
        group = state.group

    var sortDescriptors = sort || []
    if (sortDescriptors.length && state.sort) {
        data = orderBy(data, sortDescriptors)
    }

    var hasFilters = isPresent(filterDescriptor) && filter.length
    var hasGroups = isPresent(group) && group?.length
    if (!hasFilters && !hasGroups) {
        return {
            data: takeCount ? data.slice(skipCount, skipCount ? skipCount : 0 + takeCount) : data,
            total: data.length,
        }
    }
    var total = 0
    var transformers = []
    var predicate
    if (hasFilters) {
        if (filterDescriptor) {
            predicate = compileFilter(normalizeFilters(filterDescriptor))
            total = count(data, predicate)
            transformers.push(filter(predicate))
        }
    } else {
        total = data.length
    }

    if (transformers.length) {
        var transform = compose.apply(void 0, transformers)
        if (predicate) {
            var result = groupBy(data, group, transform, limit(data, predicate))
            return {
                data: result,
                total: total,
            }
        }
    }
    const newData = groupBy(data, group)
    return {
        data: newData,
        total: total,
    }
}
